import { useEffect, useState } from 'react';

interface GoogleReviewPromptProps {
  googleReviewUrl: string;
  onReviewClick?: () => void;
  onDismiss: () => void;
}

export function GoogleReviewPrompt({
  googleReviewUrl,
  onReviewClick,
  onDismiss,
}: GoogleReviewPromptProps) {
  const [isDismissed, setIsDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('google-review-dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      return;
    }

    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem('google-review-dismissed', 'true');
    setIsVisible(false);
    setTimeout(() => {
      setIsDismissed(true);
      onDismiss();
    }, 400);
  };

  const handleClick = () => {
    onReviewClick?.();
    sessionStorage.setItem('google-review-dismissed', 'true');
    setIsVisible(false);
    setTimeout(() => {
      setIsDismissed(true);
      window.open(googleReviewUrl, '_blank', 'noopener,noreferrer');
    }, 400);
  };

  if (isDismissed) return null;

  return (
    <div
      className={`fixed inset-0 z-30 flex items-center justify-center transition-opacity duration-500 ease-out ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      />
      <div
        className={`relative z-10 text-center px-8 space-y-6 max-w-sm mx-auto transition-all duration-500 ease-out ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}
      >
        <h2 className="font-serif text-2xl text-white font-light">
          ¿Te ha gustado tu experiencia?
        </h2>

        <button
          onClick={handleClick}
          className="w-full h-12 rounded-xl font-sans text-sm font-semibold tracking-wide flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
          style={{
            backgroundColor: '#fff',
            color: '#1e1e1e',
          }}
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Dejar reseña en Google
        </button>

        <button
          onClick={handleDismiss}
          className="text-sm text-white/50 hover:text-white/70 transition-colors"
        >
          Ahora no
        </button>
      </div>
    </div>
  );
}
